import type { CaseData, CmsPage, ImageData } from "@/cms/models";
import { getCaseGalleryImages } from "@/utils/case-gallery";

export type CaseImageGridLayoutName =
  | "magazine"
  | "rows"
  | "featured"
  | "mosaic";
export type CaseImageGridBreakpoint = "mobile" | "tablet" | "desktop";
export type CaseImageGridMediaKind = "photo" | "video" | "drawing";
export type CaseImageGridFilter = "all" | CaseImageGridMediaKind;
export type CaseImageGridViewer = "scroll" | "slideshow" | "none";

export interface CaseImageGridSettings {
  layout?: CaseImageGridLayoutName;
  variation?: "calm" | "lively";
  rowHeight?: "low" | "medium" | "high";
  maxPerRow?: 2 | 3 | 4 | 5;
  columns?: 3 | 4 | 5 | 6;
  bigTile?: "alternate" | "every" | "none";
  featuredStart?: "five" | "three";
  skip?: "auto" | 0 | 1 | 2 | 3 | 4 | 5;
  includeVideo?: boolean;
  includeDrawings?: boolean;
  typeMenu?: boolean;
  initialCount?: number;
  showAll?: "expand" | "step" | "viewer";
  viewer?: CaseImageGridViewer;
  captions?: "none" | "hover" | "below";
  gap?: "none" | "xs" | "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md";
}

export interface ResolvedCaseImageGridSettings
  extends Required<Omit<CaseImageGridSettings, "maxPerRow">> {
  maxPerRow?: 2 | 3 | 4 | 5;
}

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** Absent and unknown values fall back to the defaults, so stored JSON never breaks the page. */
export function resolveCaseImageGridSettings(
  specifics?: CaseImageGridSettings | null,
): ResolvedCaseImageGridSettings {
  const s = specifics ?? {};
  const initialCount =
    typeof s.initialCount === "number" &&
    Number.isFinite(s.initialCount) &&
    s.initialCount >= 0
      ? Math.floor(s.initialCount)
      : 12;
  return {
    layout: pick(
      s.layout,
      ["magazine", "rows", "featured", "mosaic"],
      "magazine",
    ),
    variation: pick(s.variation, ["calm", "lively"], "lively"),
    rowHeight: pick(s.rowHeight, ["low", "medium", "high"], "medium"),
    maxPerRow: pick(s.maxPerRow, [2, 3, 4, 5, undefined], undefined),
    columns: pick(s.columns, [3, 4, 5, 6], 4),
    bigTile: pick(s.bigTile, ["alternate", "every", "none"], "alternate"),
    featuredStart: pick(s.featuredStart, ["five", "three"], "five"),
    skip: pick(s.skip, ["auto", 0, 1, 2, 3, 4, 5], "auto"),
    includeVideo: s.includeVideo === true,
    includeDrawings: s.includeDrawings === true,
    typeMenu: s.typeMenu === true,
    initialCount,
    showAll: pick(s.showAll, ["expand", "step", "viewer"], "expand"),
    viewer: pick(s.viewer, ["scroll", "slideshow", "none"], "scroll"),
    captions: pick(s.captions, ["none", "hover", "below"], "none"),
    gap: pick(s.gap, ["none", "xs", "sm", "md", "lg"], "sm"),
    radius: pick(s.radius, ["none", "sm", "md"], "none"),
  };
}

/** The same scale as the CMS spacing tokens (`homepage-gap-*`). */
export const CASE_IMAGE_GRID_GAP_PX = { none: 0, xs: 4, sm: 8, md: 16, lg: 32 };
export const CASE_IMAGE_GRID_RADIUS_PX = { none: 0, sm: 2, md: 6 };
export const CASE_IMAGE_GRID_CAPTION_PX = 28;
export const CASE_IMAGE_GRID_MAX_CROP = 0.25;

export interface CaseImageGridItem {
  kind: CaseImageGridMediaKind;
  /** 0 when the size is unknown. */
  width: number;
  height: number;
}

export interface CaseImageGridMedia extends CaseImageGridItem {
  image?: ImageData;
  video?: string;
}

export function caseImageGridMedia(caseData: CaseData): CaseImageGridMedia[] {
  const images = getCaseGalleryImages(caseData);
  const fromImage = (
    image: ImageData,
    kind: CaseImageGridMediaKind,
  ): CaseImageGridMedia => ({
    kind,
    width: image.pixelWidth > 0 && image.pixelHeight > 0 ? image.pixelWidth : 0,
    height:
      image.pixelWidth > 0 && image.pixelHeight > 0 ? image.pixelHeight : 0,
    image,
  });
  return [
    ...images
      .filter((image) => image.namespace.endsWith("photo"))
      .map((image) => fromImage(image, "photo")),
    ...(caseData.videoUrls ?? []).filter(Boolean).map(
      (video): CaseImageGridMedia => ({
        kind: "video",
        width: 16,
        height: 9,
        video,
      }),
    ),
    ...images
      .filter((image) => image.namespace.endsWith("drawing"))
      .map((image) => fromImage(image, "drawing")),
  ];
}

// The media server fits the md, lg and xl variants into 650, 1280 and 2600 px boxes.
const VARIANT_BOX = { mdUrl: 650, lgUrl: 1280, xlUrl: 2600 } as const;

export function caseImageGridSrcSet(image: ImageData): string | undefined {
  const known = image.pixelWidth > 0 && image.pixelHeight > 0;
  const seen = new Set<string>();
  const entries: string[] = [];
  for (const key of ["mdUrl", "lgUrl", "xlUrl"] as const) {
    const url = image[key];
    if (!url || seen.has(url)) continue;
    seen.add(url);
    const box = VARIANT_BOX[key];
    const width = known
      ? Math.round(
          Math.min(
            image.pixelWidth,
            image.pixelWidth >= image.pixelHeight
              ? box
              : (box * image.pixelWidth) / image.pixelHeight,
          ),
        )
      : box;
    entries.push(`${url.replace(/ /g, "%20").replace(/,/g, "%2C")} ${width}w`);
  }
  return entries.length > 1 ? entries.join(", ") : undefined;
}

export interface CaseImageGridSkip {
  mobile: number;
  desktop: number;
}

const responsivePart = (value: string, desktop: boolean) => {
  const parts = value.split("|");
  return (desktop ? (parts[1] ?? parts[0]) : parts[0]).trim();
};
const shownByTileGallery = (value: string, max: number) => {
  const count = parseInt(value, 10);
  return Number.isNaN(count) ? 1 : Math.min(max, Math.max(1, count));
};

/**
 * Skip "auto" skips exactly the photos the page's first CaseTileGallerySection shows. Mobile and
 * desktop follow the tile gallery's own 768 px switch, which is why the result has two values.
 */
export function resolveCaseImageGridSkip(
  skip: ResolvedCaseImageGridSettings["skip"],
  page?: Pick<CmsPage, "sections"> | null,
  showsHeroVideo = false,
): CaseImageGridSkip {
  if (typeof skip === "number") return { mobile: skip, desktop: skip };
  const gallery = page?.sections?.find(
    (section) => section?.type === "CaseTileGallerySection",
  );
  if (!gallery) return { mobile: 0, desktop: 0 };
  const specifics = gallery.specifics ?? {};
  if (specifics.presentation === "cinematic") return { mobile: 1, desktop: 1 };
  if (showsHeroVideo) return { mobile: 0, desktop: 0 };
  const count =
    typeof specifics.numberOfImages === "string"
      ? specifics.numberOfImages
      : "";
  return {
    mobile: shownByTileGallery(responsivePart(count || "3", false), 3),
    desktop: shownByTileGallery(responsivePart(count || "4", true), 4),
  };
}

/** `unit` is a share of the grid's content width, `px` a fixed length: unit × width + px. */
export interface GridLength {
  unit: number;
  px: number;
}

const length = (unit: number, px = 0): GridLength => ({ unit, px });
const plus = (a: GridLength, b: GridLength) =>
  length(a.unit + b.unit, a.px + b.px);
const minus = (a: GridLength, b: GridLength) =>
  length(a.unit - b.unit, a.px - b.px);
const times = (a: GridLength, factor: number) =>
  length(a.unit * factor, a.px * factor);

export function measureGridLength(value: GridLength, width: number): number {
  return value.unit * width + value.px;
}

const round = (value: number) => Math.round(value * 10000) / 10000 || 0;

export function gridLengthCss(value: GridLength): string {
  const unit = round(value.unit * 100);
  const px = round(value.px);
  if (!px) return `${unit}cqw`;
  if (!unit) return `${px}px`;
  return `calc(${unit}cqw ${px < 0 ? "-" : "+"} ${Math.abs(px)}px)`;
}

export type CaseImageGridClass = "L" | "S" | "P" | "W" | "D" | "V";

export function caseImageGridRatio(item: CaseImageGridItem): number {
  if (item.kind === "video") return 16 / 9;
  return item.width > 0 && item.height > 0 ? item.width / item.height : 1.5;
}

export function classifyCaseImageGridItem(
  item: CaseImageGridItem,
): CaseImageGridClass {
  if (item.kind === "video") return "V";
  if (item.kind === "drawing") return "D";
  const ratio = caseImageGridRatio(item);
  if (ratio >= 2.2) return "W";
  if (ratio >= 1.15) return "L";
  if (ratio >= 0.87) return "S";
  return "P";
}

export function caseImageGridCrop(tileRatio: number, imageRatio: number) {
  return 1 - Math.min(tileRatio / imageRatio, imageRatio / tileRatio);
}

/** Unknown sizes and floor plans are shown whole, so a wrong guess never crops them. */
export function caseImageGridFit(item: CaseImageGridItem): "cover" | "contain" {
  if (item.kind === "drawing") return "contain";
  if (item.kind === "photo" && !(item.width > 0 && item.height > 0))
    return "contain";
  return "cover";
}

export interface CaseImageGridTile {
  item: number;
  x: GridLength;
  y: GridLength;
  width: GridLength;
  height: GridLength;
  fit: "cover" | "contain";
  block: number;
}

export interface CaseImageGridBlock {
  /** The number of tiles up to and including this block. */
  end: number;
  bottom: GridLength;
}

export interface CaseImageGridLayout {
  breakpoint: CaseImageGridBreakpoint;
  referenceWidth: number;
  gap: number;
  /** Space reserved below every row for captions; 0 unless captions are "below". */
  caption: number;
  tiles: CaseImageGridTile[];
  blocks: CaseImageGridBlock[];
  height: GridLength;
}

export interface CaseImageGridInput {
  items: readonly CaseImageGridItem[];
  settings: ResolvedCaseImageGridSettings;
  breakpoint: CaseImageGridBreakpoint;
  skip?: number;
  filter?: CaseImageGridFilter;
}

export const CASE_IMAGE_GRID_REFERENCE_WIDTH: Record<
  CaseImageGridBreakpoint,
  number
> = { mobile: 358, tablet: 720, desktop: 1200 };

// Content widths each breakpoint can render at; Infinity covers full-width sections on wide screens.
const RENDER_WIDTHS: Record<CaseImageGridBreakpoint, number[]> = {
  mobile: [288, 640],
  tablet: [560, 1024],
  desktop: [900, Infinity],
};

const ROW_TARGET_PX: Record<
  CaseImageGridBreakpoint,
  Record<ResolvedCaseImageGridSettings["rowHeight"], number>
> = {
  mobile: { low: 100, medium: 130, high: 180 },
  tablet: { low: 160, medium: 220, high: 300 },
  desktop: { low: 220, medium: 300, high: 400 },
};

const CELL_RATIO: Record<ResolvedCaseImageGridSettings["rowHeight"], number> = {
  low: 16 / 9,
  medium: 3 / 2,
  high: 4 / 3,
};

interface Placed {
  item: number;
  x: GridLength;
  y: GridLength;
  width: GridLength;
  height: GridLength;
  fit: "cover" | "contain";
}

interface Block {
  tiles: Placed[];
  height: GridLength;
}

interface Context {
  items: readonly CaseImageGridItem[];
  settings: ResolvedCaseImageGridSettings;
  breakpoint: CaseImageGridBreakpoint;
  width: number;
  gapX: number;
  gapY: number;
  caption: number;
}

const ratioOf = (ctx: Context, item: number) =>
  caseImageGridRatio(ctx.items[item]);
const classOf = (ctx: Context, item: number | undefined) =>
  item === undefined ? undefined : classifyCaseImageGridItem(ctx.items[item]);
const landscape = (cls?: CaseImageGridClass) => cls === "L" || cls === "S";
const at = (ctx: Context, value: GridLength) =>
  measureGridLength(value, ctx.width);

/**
 * True when `a` is longer than `b` at every width the breakpoint renders. Caps and "not stretched"
 * rows only apply then, so a capped row can never grow wider than the grid at another width.
 */
function exceeds(ctx: Context, a: GridLength, b: GridLength) {
  return [ctx.width, ...RENDER_WIDTHS[ctx.breakpoint]].every((width) =>
    width === Infinity
      ? a.unit > b.unit || (a.unit === b.unit && a.px > b.px)
      : measureGridLength(a, width) > measureGridLength(b, width),
  );
}

function tileRatioAt(tile: Placed, width: number) {
  if (width === Infinity) return tile.width.unit / tile.height.unit;
  return (
    measureGridLength(tile.width, width) / measureGridLength(tile.height, width)
  );
}

function withinCropLimit(ctx: Context, block: Block) {
  return block.tiles.every((tile) => {
    const item = ctx.items[tile.item];
    if (tile.fit !== "cover" || item.kind !== "photo") return true;
    const imageRatio = caseImageGridRatio(item);
    return [ctx.width, ...RENDER_WIDTHS[ctx.breakpoint]].every(
      (width) =>
        caseImageGridCrop(tileRatioAt(tile, width), imageRatio) <=
        CASE_IMAGE_GRID_MAX_CROP + 1e-9,
    );
  });
}

function placed(
  ctx: Context,
  item: number,
  x: GridLength,
  y: GridLength,
  width: GridLength,
  height: GridLength,
): Placed {
  return { item, x, y, width, height, fit: caseImageGridFit(ctx.items[item]) };
}

/** Every tile keeps its own ratio; the row height follows, optionally capped and then centred. */
function justified(
  ctx: Context,
  items: number[],
  options: { maxHeight?: GridLength; height?: GridLength } = {},
): Block {
  const ratios = items.map((item) => ratioOf(ctx, item));
  const sum = ratios.reduce((total, ratio) => total + ratio, 0);
  const gaps = ctx.gapX * (items.length - 1);
  let height = options.height ?? length(1 / sum, -gaps / sum);
  if (options.maxHeight && exceeds(ctx, height, options.maxHeight))
    height = options.maxHeight;
  const rowWidth = plus(times(height, sum), length(0, gaps));
  let x = options.height ? length(0) : times(minus(length(1), rowWidth), 0.5);
  const tiles = items.map((item, index) => {
    const width = times(height, ratios[index]);
    const tile = placed(ctx, item, x, length(0), width, height);
    x = plus(plus(x, width), length(0, ctx.gapX));
    return tile;
  });
  return { tiles, height };
}

function full(
  ctx: Context,
  item: number,
  ratio: number,
  maxHeight?: GridLength,
): Block {
  let height = length(1 / ratio);
  let width = length(1);
  let x = length(0);
  if (maxHeight && exceeds(ctx, height, maxHeight)) {
    height = maxHeight;
    width = times(height, ratio);
    x = times(minus(length(1), width), 0.5);
  }
  return { tiles: [placed(ctx, item, x, length(0), width, height)], height };
}

/**
 * One full-height tile beside two stacked tiles, all at their own ratio. Solving
 * tallWidth / tallRatio = stackWidth × (1/a + 1/b) + gapY keeps the edges flush at every width.
 */
function stack(
  ctx: Context,
  tall: number,
  pair: [number, number],
  side: "left" | "right",
): Block {
  const tallRatio = ratioOf(ctx, tall);
  const [first, second] = pair;
  const inverse = 1 / ratioOf(ctx, first) + 1 / ratioOf(ctx, second);
  const divisor = 1 + tallRatio * inverse;
  const stackWidth = length(
    1 / divisor,
    -(ctx.gapX + tallRatio * ctx.gapY) / divisor,
  );
  const tallWidth = minus(length(1, -ctx.gapX), stackWidth);
  const tallHeight = times(tallWidth, 1 / tallRatio);
  const firstHeight = times(stackWidth, 1 / ratioOf(ctx, first));
  const secondHeight = times(stackWidth, 1 / ratioOf(ctx, second));
  const stackX =
    side === "left" ? plus(tallWidth, length(0, ctx.gapX)) : length(0);
  const tallX =
    side === "left" ? length(0) : plus(stackWidth, length(0, ctx.gapX));
  const tallTile = placed(ctx, tall, tallX, length(0), tallWidth, tallHeight);
  const stacked = [
    placed(ctx, first, stackX, length(0), stackWidth, firstHeight),
    placed(
      ctx,
      second,
      stackX,
      plus(firstHeight, length(0, ctx.gapY)),
      stackWidth,
      secondHeight,
    ),
  ];
  return {
    tiles: side === "left" ? [tallTile, ...stacked] : [...stacked, tallTile],
    height: tallHeight,
  };
}

class Flow {
  readonly tiles: CaseImageGridTile[] = [];
  readonly blocks: CaseImageGridBlock[] = [];
  private y = length(0);
  private videosPlaced = false;

  constructor(
    private readonly ctx: Context,
    private readonly videos: number[],
    private readonly videoMaxHeight?: GridLength,
  ) {}

  add(block: Block) {
    const index = this.blocks.length;
    for (const tile of block.tiles)
      this.tiles.push({ ...tile, y: plus(this.y, tile.y), block: index });
    const bottom = plus(this.y, block.height);
    this.blocks.push({ end: this.tiles.length, bottom });
    this.y = plus(bottom, length(0, this.ctx.gapY));
    if (this.blocks.length === 1) this.placeVideos();
  }

  private placeVideos() {
    if (this.videosPlaced) return;
    this.videosPlaced = true;
    for (const video of this.videos)
      this.add(full(this.ctx, video, 16 / 9, this.videoMaxHeight));
  }

  finish(drawings: number[]): Omit<CaseImageGridLayout, "breakpoint"> {
    this.placeVideos();
    for (const block of drawingBlocks(this.ctx, drawings)) this.add(block);
    const last = this.blocks.at(-1);
    return {
      referenceWidth: this.ctx.width,
      gap: this.ctx.gapX,
      caption: this.ctx.caption,
      tiles: this.tiles,
      blocks: this.blocks,
      height: last ? plus(last.bottom, length(0, this.ctx.caption)) : length(0),
    };
  }
}

function drawingBlocks(ctx: Context, drawings: number[]): Block[] {
  if (!drawings.length) return [];
  const columns =
    ctx.breakpoint === "desktop" && drawings.length !== 4 && drawings.length > 2
      ? 3
      : 2;
  const cellWidth = length(1 / columns, (-ctx.gapX * (columns - 1)) / columns);
  const cellHeight = times(cellWidth, 3 / 4);
  const blocks: Block[] = [];
  for (let start = 0; start < drawings.length; start += columns) {
    const row = drawings.slice(start, start + columns);
    const rowWidth = plus(
      times(cellWidth, row.length),
      length(0, ctx.gapX * (row.length - 1)),
    );
    const offset = times(minus(length(1), rowWidth), 0.5);
    blocks.push({
      height: cellHeight,
      tiles: row.map((item, index) =>
        placed(
          ctx,
          item,
          plus(offset, times(plus(cellWidth, length(0, ctx.gapX)), index)),
          length(0),
          cellWidth,
          cellHeight,
        ),
      ),
    });
  }
  return blocks;
}

function rowTarget(
  ctx: Context,
  rowHeight: ResolvedCaseImageGridSettings["rowHeight"],
) {
  return length(
    ROW_TARGET_PX[ctx.breakpoint][rowHeight] /
      CASE_IMAGE_GRID_REFERENCE_WIDTH[ctx.breakpoint],
  );
}

/**
 * The next justified row from `start`. A row closes at the break whose height lands closest to
 * the target; a panorama always stands alone.
 */
function nextRow(
  ctx: Context,
  queue: number[],
  start: number,
  target: GridLength,
  maxPerRow?: number,
): number[] {
  const targetPx = at(ctx, target);
  const naturalHeight = (row: number[]) =>
    (ctx.width - ctx.gapX * (row.length - 1)) /
    row.reduce((sum, item) => sum + ratioOf(ctx, item), 0);
  const row: number[] = [];
  for (let index = start; index < queue.length; index += 1) {
    const item = queue[index];
    if (classOf(ctx, item) === "W") return row.length ? row : [item];
    const candidate = [...row, item];
    const natural = candidate.reduce(
      (sum, member) => sum + ratioOf(ctx, member) * targetPx,
      ctx.gapX * (candidate.length - 1),
    );
    if (natural >= ctx.width) {
      if (
        row.length &&
        Math.abs(naturalHeight(row) - targetPx) <
          Math.abs(naturalHeight(candidate) - targetPx)
      )
        return row;
      return candidate;
    }
    row.push(item);
    if (maxPerRow && row.length >= maxPerRow) return row;
  }
  return row;
}

/**
 * Justified rows. A row that closes a run (the last row, or the row before a panorama) is
 * rebalanced with the row before it and, if still underfull, keeps the target height instead of
 * being stretched.
 */
function rowBlocks(
  ctx: Context,
  items: number[],
  target: GridLength,
  maxPerRow?: number,
): Block[] {
  const maxHeight = times(target, 1.6);
  const rows: number[][] = [];
  for (let index = 0; index < items.length; ) {
    const row = nextRow(ctx, items, index, target, maxPerRow);
    rows.push(row);
    index += row.length;
  }
  const isPanorama = (row?: number[]) =>
    row !== undefined && classOf(ctx, row[0]) === "W";
  const closes = (index: number) =>
    !isPanorama(rows[index]) &&
    (index === rows.length - 1 || isPanorama(rows[index + 1]));
  for (let index = rows.length - 1; index > 0; index -= 1)
    if (closes(index) && !isPanorama(rows[index - 1]))
      rebalance(ctx, rows, index, target, maxPerRow);
  return rows.map((row, index) => {
    if (isPanorama(row))
      return full(ctx, row[0], ratioOf(ctx, row[0]), maxHeight);
    const natural = justified(ctx, row);
    if (closes(index) && exceeds(ctx, natural.height, times(target, 1.15)))
      return justified(ctx, row, { height: target });
    return justified(ctx, row, { maxHeight });
  });
}

/**
 * Moves items between rows[index - 1] and an underfull rows[index], or merges them, so a closing
 * row is not a lone widow when a better split exists. An underfull closing row is drawn at the
 * target height anyway, so only a widow counts against it.
 */
function rebalance(
  ctx: Context,
  rows: number[][],
  index: number,
  target: GridLength,
  maxPerRow?: number,
) {
  const targetPx = at(ctx, target);
  const height = (row: number[]) => at(ctx, justified(ctx, row).height);
  if (height(rows[index]) <= targetPx * 1.15) return;
  const deviation = (row: number[]) =>
    Math.abs(height(row) - targetPx) / targetPx;
  const score = (a: number[], b: number[]) =>
    Math.max(deviation(a), b.length === 1 ? deviation(b) : 0);
  const all = [...rows[index - 1], ...rows[index]];
  let best = {
    score: score(rows[index - 1], rows[index]),
    split: rows[index - 1].length,
  };
  for (let split = 1; split <= all.length; split += 1) {
    const a = all.slice(0, split);
    const b = all.slice(split);
    if (maxPerRow && (a.length > maxPerRow || b.length > maxPerRow)) continue;
    const candidate = score(a, b);
    if (candidate < best.score - 1e-9) best = { score: candidate, split };
  }
  const merged = [all.slice(0, best.split), all.slice(best.split)].filter(
    (row) => row.length,
  );
  rows.splice(index - 1, 2, ...merged);
}

interface MosaicGrid {
  columns: number;
  cellWidth: GridLength;
  cellHeight: GridLength;
  blockHeight: GridLength;
}

function mosaicGrid(
  ctx: Context,
  columns: number,
  cellRatio: number,
): MosaicGrid {
  const cellWidth = length(1 / columns, (-ctx.gapX * (columns - 1)) / columns);
  const cellHeight = times(cellWidth, 1 / cellRatio);
  return {
    columns,
    cellWidth,
    cellHeight,
    blockHeight: plus(times(cellHeight, 2), length(0, ctx.gapY)),
  };
}

interface MosaicAttempt {
  block: Block;
  next: number;
  queue: number[];
  moved: Set<number>;
}

/**
 * Two rows on an N-column grid: an optional 2×2 tile, then per column one portrait (1×2) or two
 * stacked landscapes. A portrait may swap with the landscape before it, but no item moves twice.
 */
function mosaicBlock(
  ctx: Context,
  grid: MosaicGrid,
  queue: number[],
  start: number,
  movedBefore: Set<number>,
  bigColumn?: number,
): MosaicAttempt | null {
  const local = queue.slice();
  const moved = new Set(movedBefore);
  const tiles: Placed[] = [];
  const cell = (
    item: number,
    column: number,
    spanColumns: number,
    spanRows: number,
    row: number,
  ) =>
    tiles.push(
      placed(
        ctx,
        item,
        times(plus(grid.cellWidth, length(0, ctx.gapX)), column),
        times(plus(grid.cellHeight, length(0, ctx.gapY)), row),
        plus(
          times(grid.cellWidth, spanColumns),
          length(0, ctx.gapX * (spanColumns - 1)),
        ),
        plus(
          times(grid.cellHeight, spanRows),
          length(0, ctx.gapY * (spanRows - 1)),
        ),
      ),
    );
  let next = start;
  if (bigColumn !== undefined) {
    if (!landscape(classOf(ctx, local[next]))) return null;
    cell(local[next], bigColumn, 2, 2, 0);
    next += 1;
  }
  for (let column = 0; column < grid.columns; column += 1) {
    if (
      bigColumn !== undefined &&
      column >= bigColumn &&
      column < bigColumn + 2
    )
      continue;
    const first = local[next];
    const firstClass = classOf(ctx, first);
    if (first === undefined || firstClass === "W") return null;
    if (firstClass === "P") {
      cell(first, column, 1, 2, 0);
      next += 1;
      continue;
    }
    const second = local[next + 1];
    const secondClass = classOf(ctx, second);
    if (second !== undefined && landscape(secondClass)) {
      cell(first, column, 1, 1, 0);
      cell(second, column, 1, 1, 1);
      next += 2;
      continue;
    }
    if (
      second !== undefined &&
      secondClass === "P" &&
      !moved.has(first) &&
      !moved.has(second)
    ) {
      local[next] = second;
      local[next + 1] = first;
      moved.add(first).add(second);
      cell(second, column, 1, 2, 0);
      next += 1;
      continue;
    }
    return null;
  }
  const block = { tiles, height: grid.blockHeight };
  return withinCropLimit(ctx, block)
    ? { block, next, queue: local, moved }
    : null;
}

function mosaicLayout(ctx: Context, flow: Flow, photos: number[]) {
  const mobile = ctx.breakpoint === "mobile";
  const columns = mobile
    ? 2
    : ctx.breakpoint === "tablet"
      ? Math.min(ctx.settings.columns, 3)
      : ctx.settings.columns;
  const grid = mosaicGrid(ctx, columns, CELL_RATIO[ctx.settings.rowHeight]);
  const leftoverTarget = times(grid.cellHeight, 1.3);
  const magazine = mobile ? new MobileMagazine(ctx) : undefined;
  let queue = photos.slice();
  let moved = new Set<number>();
  let index = 0;
  let blockCount = 0;
  let side = 0;
  while (index < queue.length) {
    const first = queue[index];
    if (classOf(ctx, first) === "W") {
      flow.add(full(ctx, first, ratioOf(ctx, first), grid.blockHeight));
      index += 1;
      continue;
    }
    const wantBig =
      ctx.settings.bigTile !== "none" &&
      columns >= 3 &&
      landscape(classOf(ctx, first)) &&
      (ctx.settings.bigTile === "every" || blockCount % 2 === 0);
    const bigColumn = side === 0 ? 0 : columns - 2;
    const attempt =
      (wantBig && mosaicBlock(ctx, grid, queue, index, moved, bigColumn)) ||
      mosaicBlock(ctx, grid, queue, index, moved);
    if (attempt) {
      flow.add(attempt.block);
      if (
        wantBig &&
        attempt.block.tiles[0].width.unit > grid.cellWidth.unit * 1.5
      )
        side ^= 1;
      queue = attempt.queue;
      moved = attempt.moved;
      index = attempt.next;
    } else if (magazine) {
      index = magazine.step(flow, queue, index);
    } else {
      const row = nextRow(ctx, queue, index, leftoverTarget);
      const following = queue[index + row.length];
      const last = following === undefined || classOf(ctx, following) === "W";
      const natural = justified(ctx, row);
      flow.add(
        last && exceeds(ctx, natural.height, times(leftoverTarget, 1.15))
          ? justified(ctx, row, { height: leftoverTarget })
          : justified(ctx, row, { maxHeight: times(leftoverTarget, 1.6) }),
      );
      index += row.length;
    }
    blockCount += 1;
  }
}

/** The mobile catalogue from the approved mobile board: full width or a pair, portraits paired. */
class MobileMagazine {
  private modules = 0;

  constructor(private readonly ctx: Context) {}

  step(flow: Flow, queue: number[], index: number): number {
    const ctx = this.ctx;
    const a = queue[index];
    const b = queue[index + 1];
    const aClass = classOf(ctx, a);
    const bClass = classOf(ctx, b);
    const portraitPairFollows =
      bClass === "P" && classOf(ctx, queue[index + 2]) === "P";
    const pairMax = length(300 / 342);
    let consumed = 1;
    if (aClass === "W") flow.add(full(ctx, a, ratioOf(ctx, a)));
    else if (
      b !== undefined &&
      bClass !== "W" &&
      (aClass === "P" ||
        (bClass === "P" && !portraitPairFollows) ||
        (this.modules % 2 === 1 && landscape(bClass) && !portraitPairFollows))
    ) {
      flow.add(justified(ctx, [a, b], { maxHeight: pairMax }));
      consumed = 2;
    } else flow.add(this.single(a));
    this.modules += 1;
    return index + consumed;
  }

  private single(item: number): Block {
    const ctx = this.ctx;
    const portrait = classOf(ctx, item) === "P";
    const cover = full(ctx, item, portrait ? 4 / 5 : 3 / 2);
    if (
      caseImageGridFit(ctx.items[item]) === "cover" &&
      withinCropLimit(ctx, cover)
    )
      return cover;
    return justified(ctx, [item], {
      maxHeight: length(portrait ? 1.25 : 1 / 1.5),
    });
  }
}

/** Orientation-aware modules for tablet and desktop (board A). */
function magazineLayout(ctx: Context, flow: Flow, photos: number[]) {
  const lively = ctx.settings.variation === "lively";
  const tallMax = length(560 / 1200);
  const rowMax = length(520 / 1200);
  const n = photos.length;
  let modules = 0;
  let sinceHero = 0;
  let side: "left" | "right" = "left";
  const alternate = () => {
    const current = side;
    side = side === "left" ? "right" : "left";
    return current;
  };
  const hero = (item: number) => {
    const block = full(ctx, item, 16 / 9);
    return caseImageGridFit(ctx.items[item]) === "contain" ||
      withinCropLimit(ctx, block)
      ? block
      : null;
  };
  const row = (items: number[]) =>
    justified(ctx, items, {
      maxHeight:
        items.filter((item) => classOf(ctx, item) === "P").length > 1
          ? tallMax
          : rowMax,
    });
  const single = (item: number) =>
    (landscape(classOf(ctx, item)) && lively && hero(item)) ||
    justified(ctx, [item], {
      maxHeight: classOf(ctx, item) === "P" ? tallMax : rowMax,
    });
  /** ppp, ppl, lpp, pll, lpl, llp, trio – or undefined when the three need another module. */
  const threeModule = (
    [a, b, c]: number[],
    closing: boolean,
  ): Block | undefined => {
    const [ac, bc, cc] = [classOf(ctx, a), classOf(ctx, b), classOf(ctx, c)];
    if (bc === "W" || cc === "W") return undefined;
    if (ac === "P" && landscape(bc) && landscape(cc))
      return stack(ctx, a, [b, c], "left");
    if (landscape(ac) && bc === "P" && landscape(cc))
      return stack(ctx, b, [a, c], alternate());
    if (landscape(ac) && landscape(bc) && cc === "P")
      return stack(ctx, c, [a, b], "right");
    if (landscape(ac) && landscape(bc) && landscape(cc)) {
      if (!lively) return row([a, b, c]);
      if (!closing && modules % 2 === 1) return undefined;
      return alternate() === "left"
        ? stack(ctx, a, [b, c], "left")
        : stack(ctx, c, [a, b], "right");
    }
    return row([a, b, c]);
  };
  /** The last two to four photos, so the grid never ends on a lone photo when it can be paired. */
  const closing = (items: number[]): Block[] => {
    if (classOf(ctx, items[0]) === "W")
      return classOf(ctx, items[1]) === "P"
        ? [row(items)]
        : [full(ctx, items[0], ratioOf(ctx, items[0])), single(items[1])];
    if (items.length === 2) return [row(items)];
    if (items.length === 3) return [threeModule(items, true) ?? row(items)];
    const portraits = items.filter((item) => classOf(ctx, item) === "P").length;
    return portraits >= 3
      ? [row(items)]
      : [row(items.slice(0, 2)), row(items.slice(2))];
  };
  let index = 0;
  while (index < n) {
    const [a, b, c] = [photos[index], photos[index + 1], photos[index + 2]];
    const [ac, bc, cc] = [classOf(ctx, a), classOf(ctx, b), classOf(ctx, c)];
    const leavesLone = (count: number) =>
      n - (index + count) === 1 && classOf(ctx, photos[n - 1]) !== "W";
    const strandsNext = landscape(bc) && (c === undefined || cc === "W");
    const heroBlock =
      landscape(ac) &&
      !strandsNext &&
      (modules === 0 || (lively && sinceHero >= 3 && bc !== "P"))
        ? hero(a)
        : null;
    let blocks: Block[];
    let count: number;
    const module3 = c !== undefined ? threeModule([a, b, c], false) : undefined;
    if (ac === "W") [blocks, count] = [[full(ctx, a, ratioOf(ctx, a))], 1];
    else if (heroBlock) [blocks, count] = [[heroBlock], 1];
    else if (module3) [blocks, count] = [[module3], 3];
    else if (b !== undefined && bc !== "W")
      [blocks, count] = [[row([a, b])], 2];
    else [blocks, count] = [[single(a)], 1];
    const heroUsed = blocks[0] === heroBlock;
    if (leavesLone(count) && n - index <= 4) {
      blocks = closing(photos.slice(index));
      count = n - index;
    }
    for (const block of blocks) flow.add(block);
    sinceHero = heroUsed && count === 1 ? 0 : sinceHero + 1;
    modules += 1;
    index += count;
  }
}

function rowsLayout(
  ctx: Context,
  flow: Flow,
  photos: number[],
  rowHeight: ResolvedCaseImageGridSettings["rowHeight"],
  maxPerRow?: number,
) {
  for (const block of rowBlocks(
    ctx,
    photos,
    rowTarget(ctx, rowHeight),
    maxPerRow,
  ))
    flow.add(block);
}

/** Board C: one mosaic block with the first landscape as the big tile, then the rest. */
function featuredLayout(ctx: Context, flow: Flow, photos: number[]) {
  const mobile = ctx.breakpoint === "mobile";
  const columns = mobile
    ? 2
    : ctx.breakpoint === "desktop" && ctx.settings.featuredStart === "five"
      ? 4
      : 3;
  const grid = mosaicGrid(ctx, columns, 3 / 2);
  let queue = photos.slice();
  let moved = new Set<number>();
  if (classOf(ctx, queue[0]) === "P" && landscape(classOf(ctx, queue[1]))) {
    queue = [queue[1], queue[0], ...queue.slice(2)];
    moved = new Set([queue[0], queue[1]]);
  }
  const attempt =
    mosaicBlock(ctx, grid, queue, 0, moved, 0) ??
    (mobile ? null : mosaicBlock(ctx, grid, photos, 0, new Set()));
  let rest = photos;
  if (attempt) {
    flow.add(attempt.block);
    rest = attempt.queue.slice(attempt.next);
  }
  if (mobile) {
    const magazine = new MobileMagazine(ctx);
    for (let index = 0; index < rest.length; )
      index = magazine.step(flow, rest, index);
  } else rowsLayout(ctx, flow, rest, "medium");
}

/**
 * Lays out the grid for one breakpoint. Pure and deterministic: the server runs it per
 * breakpoint, and the client only reruns it when the type menu filters.
 */
export function layoutCaseImageGrid(
  input: CaseImageGridInput,
): CaseImageGridLayout {
  const { items, settings, breakpoint } = input;
  const filter = input.filter ?? "all";
  const gap = CASE_IMAGE_GRID_GAP_PX[settings.gap];
  const caption =
    settings.captions === "below" ? CASE_IMAGE_GRID_CAPTION_PX : 0;
  const ctx: Context = {
    items,
    settings,
    breakpoint,
    width: CASE_IMAGE_GRID_REFERENCE_WIDTH[breakpoint],
    gapX: gap,
    gapY: gap + caption,
    caption,
  };
  const indices = (kind: CaseImageGridMediaKind) =>
    items.flatMap((item, index) => (item.kind === kind ? [index] : []));
  const wants = (kind: CaseImageGridMediaKind) =>
    filter === "all" ? true : filter === kind;
  const photos = wants("photo")
    ? indices("photo").slice(Math.max(0, input.skip ?? 0))
    : [];
  const videos =
    wants("video") && (settings.includeVideo || filter === "video")
      ? indices("video")
      : [];
  const drawings =
    wants("drawing") && (settings.includeDrawings || filter === "drawing")
      ? indices("drawing")
      : [];
  const layout = settings.layout;
  const target = rowTarget(ctx, settings.rowHeight);
  const videoMaxHeight =
    layout === "mosaic" && breakpoint !== "mobile"
      ? mosaicGrid(
          ctx,
          breakpoint === "tablet"
            ? Math.min(settings.columns, 3)
            : settings.columns,
          CELL_RATIO[settings.rowHeight],
        ).blockHeight
      : layout === "rows"
        ? times(target, 1.6)
        : layout === "featured" && breakpoint !== "mobile"
          ? times(rowTarget(ctx, "medium"), 1.6)
          : undefined;
  const flow = new Flow(ctx, videos, videoMaxHeight);
  if (layout === "rows")
    rowsLayout(ctx, flow, photos, settings.rowHeight, settings.maxPerRow);
  else if (layout === "mosaic") mosaicLayout(ctx, flow, photos);
  else if (layout === "featured") featuredLayout(ctx, flow, photos);
  else if (breakpoint === "mobile") {
    const magazine = new MobileMagazine(ctx);
    for (let index = 0; index < photos.length; )
      index = magazine.step(flow, photos, index);
  } else magazineLayout(ctx, flow, photos);
  return { breakpoint, ...flow.finish(drawings) };
}

export type CaseImageGridVariant = "m" | "s" | "t" | "d";
export const CASE_IMAGE_GRID_VARIANTS: CaseImageGridVariant[] = [
  "m",
  "s",
  "t",
  "d",
];

/**
 * m < 640 px, s 640–767 px, t 768–1023 px, d ≥ 1024 px. s exists only when the skip differs between
 * mobile and desktop, because the tile gallery switches its image count at 768 px.
 */
export type CaseImageGridVariants = Record<
  "m" | "t" | "d",
  CaseImageGridLayout
> & { s?: CaseImageGridLayout };

export function layoutCaseImageGridVariants(
  items: readonly CaseImageGridItem[],
  settings: ResolvedCaseImageGridSettings,
  skip: CaseImageGridSkip,
  filter: CaseImageGridFilter = "all",
): CaseImageGridVariants {
  const run = (breakpoint: CaseImageGridBreakpoint, count: number) =>
    layoutCaseImageGrid({ items, settings, breakpoint, skip: count, filter });
  return {
    m: run("mobile", skip.mobile),
    ...(skip.mobile !== skip.desktop ? { s: run("tablet", skip.mobile) } : {}),
    t: run("tablet", skip.desktop),
    d: run("desktop", skip.desktop),
  };
}

/**
 * The number of blocks to show before »Vis alle«: the block boundary nearest `target` tiles (ties
 * keep fewer). A step can require at least `after` blocks so every click shows more.
 */
export function caseImageGridCut(
  layout: CaseImageGridLayout,
  target: number,
  after = 0,
): number {
  const { blocks } = layout;
  if (target <= 0 || layout.tiles.length < 4) return blocks.length;
  let best = Math.min(blocks.length, after + 1);
  for (let count = best; count <= blocks.length; count += 1)
    if (
      Math.abs(blocks[count - 1].end - target) <
      Math.abs(blocks[best - 1].end - target)
    )
      best = count;
  return best;
}

export function caseImageGridCutHeight(
  layout: CaseImageGridLayout,
  blocks: number,
): GridLength {
  if (blocks >= layout.blocks.length) return layout.height;
  return plus(layout.blocks[blocks - 1].bottom, length(0, layout.caption));
}

/** The fade over the last visible block: 60 % of its height, which the renderer caps at 140 px. */
export function caseImageGridFadeHeight(
  layout: CaseImageGridLayout,
  blocks: number,
): GridLength {
  const index = Math.min(blocks, layout.blocks.length) - 1;
  if (index < 0) return length(0);
  const top =
    index === 0
      ? length(0)
      : plus(
          layout.blocks[index - 1].bottom,
          length(0, layout.gap + layout.caption),
        );
  return times(
    plus(minus(layout.blocks[index].bottom, top), length(0, layout.caption)),
    0.6,
  );
}

export function caseImageGridCounts(
  items: readonly CaseImageGridItem[],
  settings: ResolvedCaseImageGridSettings,
  skip: number,
): Record<CaseImageGridMediaKind, number> {
  const count = (kind: CaseImageGridMediaKind) =>
    items.filter((item) => item.kind === kind).length;
  return {
    photo: Math.max(0, count("photo") - skip),
    video: settings.includeVideo ? count("video") : 0,
    drawing: settings.includeDrawings ? count("drawing") : 0,
  };
}
